import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { AuthService, TenantContextService } from 'shared/core';
import { KF_AUTH_CONFIG } from '../auth/kf-auth.config';
import { homeRouteFor, KF_APP_CONFIG } from './kf-app.config';

/**
 * The way in, for every vertical: one button, because the platform has one
 * identity provider.
 *
 * This is rexine's landing page and its sign-in page folded into one — they were
 * two routes with one job between them, `/welcome` showing a card with a button
 * that navigated to `/login`, which showed a card with a button that started the
 * redirect. Both routes still exist and both render this, so every bookmark and
 * every registered redirect URI keeps working.
 *
 * What it does, in the order it decides:
 *
 *  • A signed-in visitor never sees it. With a company already chosen they go
 *    straight in; without one, to the picker. That covers the return leg of a
 *    redirect sign-in, which lands on the application's registered URI and not
 *    here.
 *  • An unauthenticated visitor arrives with whatever the last session left
 *    behind. A half-written session record is worse than none — it makes the
 *    shell believe a session exists — so it is cleared here, along with the
 *    tenant context, before the button is offered.
 *
 * The Admin API exchange is deliberately NOT here: it belongs to the picker,
 * which runs it on every visit. This page knows nothing about companies.
 */
@Component({
  selector: 'kf-login',
  imports: [NzButtonModule, NzIconModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.less',
})
export class LoginComponent implements OnInit {
  readonly app = inject(KF_APP_CONFIG);
  private readonly auth = inject(KF_AUTH_CONFIG);
  private readonly msal = inject(MsalService);
  private readonly authService = inject(AuthService);
  private readonly context = inject(TenantContextService);
  private readonly router = inject(Router);

  readonly year = new Date().getFullYear();

  ngOnInit(): void {
    if (this.msal.instance.getAllAccounts().length > 0) {
      void this.router.navigate([
        this.context.hasTenantSelected() ? homeRouteFor(this.app, this.context.getUnitType()) : '/tenant-select',
      ]);
      return;
    }

    this.clearStaleSession();
  }

  signIn(): void {
    this.msal.loginRedirect({ scopes: [this.auth.apiScope] });
  }

  /**
   * A session record is the three keys together. Any one missing means the
   * previous session ended half-way — a closed tab mid-redirect, a cleared MSAL
   * cache — and the remainder would make the shell act as though someone were
   * signed in. Rexine has cleared them here since before the tenant picker
   * existed; the tenant context goes with them, because it is the same session.
   */
  private clearStaleSession(): void {
    const complete =
      localStorage.getItem('expiresOn') &&
      localStorage.getItem('userName') &&
      localStorage.getItem('homeAccountId');
    if (complete) return;

    this.authService.clearLocalDataStorage();
    this.context.clearTenantContext();
  }
}
